"use client";
import { useEffect, useState } from 'react';
import { getMergedUtmParams, UTM_SOURCE } from '@/lib/utm';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    fbq?: (
      action: 'track' | 'init',
      eventName: string,
      params?: Record<string, any>
    ) => void;
    ttq?: {
      track: (eventName: string, params?: Record<string, any>) => void;
    };
  }
}

const LINE_URL = "https://line.me/R/ti/p/@977bzclo";
const REDIRECT_DELAY = 300;

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * デバッグログ（開発環境のみ）
 */
const debugLog = (message: string, data?: any) => {
  if (isDevelopment) {
    console.log(`[LineRedirect] ${message}`, data || '');
  }
};

/**
 * イベント送信のエラーハンドリング
 */
const safeTrackEvent = (
  name: string,
  fn: () => void
): void => {
  try {
    fn();
    debugLog(`Event sent: ${name}`);
  } catch (error) {
    if (isDevelopment) {
      console.error(`Failed to send event: ${name}`, error);
    }
  }
};

export default function LineRedirect() {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // UTMパラメータを取得（URLパラメータ優先、なければsessionStorageから）
    const utmParams = getMergedUtmParams();

    debugLog('UTM Params', utmParams);

    // GA4: line_click イベント送信（全メディア共通）
    safeTrackEvent('GA4: line_click', () => {
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag("event", "line_click", {
          event_category: "cta",
          event_label: "ad_redirect",
          link_type: "line",
          utm_source: utmParams.source,
          utm_medium: utmParams.medium,
          utm_campaign: utmParams.campaign,
          utm_content: utmParams.content,
        });
      }
    });

    // メディア別にイベント送信
    const mediaSource = utmParams.source;
    
    if (mediaSource === UTM_SOURCE.META) {
      safeTrackEvent('Meta: Lead', () => {
        if (typeof window !== 'undefined' && window.fbq) {
          window.fbq('track', 'Lead', {
            content_name: 'LINE Add',
            content_category: 'CTA',
            utm_campaign: utmParams.campaign,
            utm_content: utmParams.content,
          });
        }
      });
    } else if (mediaSource === UTM_SOURCE.TIKTOK) {
      safeTrackEvent('TikTok: CompleteRegistration', () => {
        if (typeof window !== 'undefined' && window.ttq) {
          window.ttq.track('CompleteRegistration', {
            contents: [{
              content_id: 'line_add',
              content_type: 'button',
              content_name: 'LINE Add'
            }],
            value: 0,
            currency: 'JPY',
            utm_campaign: utmParams.campaign,
            utm_content: utmParams.content,
          });
        }
      });
    } else {
      // utm_sourceが不明な場合のフォールバック
      debugLog('Unknown utm_source, sending both events', { source: mediaSource });
      
      safeTrackEvent('Meta: Lead (fallback)', () => {
        if (typeof window !== 'undefined' && window.fbq) {
          window.fbq('track', 'Lead');
        }
      });
      
      safeTrackEvent('TikTok: CompleteRegistration (fallback)', () => {
        if (typeof window !== 'undefined' && window.ttq) {
          window.ttq.track('CompleteRegistration', {
            contents: [{
              content_id: 'line_add',
              content_type: 'button',
              content_name: 'LINE Add'
            }],
            value: 0,
            currency: 'JPY'
          });
        }
      });
    }

    // リダイレクト
    const timer = setTimeout(() => {
      try {
        window.location.href = LINE_URL;
      } catch (err) {
        setError('リダイレクトに失敗しました');
        if (isDevelopment) {
          console.error('Redirect failed:', err);
        }
      }
    }, REDIRECT_DELAY);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center text-white">
          <p className="text-lg mb-4 text-red-400">{error}</p>
          <a 
            href={LINE_URL}
            className="text-cyan-400 underline"
          >
            こちらからLINEへ移動
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center text-white">
        <p className="text-lg mb-4">LINEへリダイレクト中...</p>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
      </div>
    </div>
  );
}

